import { inject, Injectable, signal } from "@angular/core";
import { IChatSession, IChatSessionConfiguration, IMessage, CreateMessageParams } from "../../../interfaces/i-chat-session";
import { ISensitivityLabel } from "../../../interfaces/i-sensitivity-label";
import { mapLegacySensitivityLabel, toISensitivityLabel } from "../../../utilities/sensitivity-label.utils";
import { ProfileService } from "../../../services/profile.service";
import { ApiService } from "../../../services/api.service";
import { catchError, EMPTY, firstValueFrom, forkJoin, map, of, Subscription, switchMap, takeWhile, timer } from "rxjs";
import { ChatSessionService } from "./chat-session.service";
import { ActionType } from "../../../types/app.types";
import { AlertService } from "../../../shared/alert/alert.service";
import { AlertEnum } from "../../../shared/alert/alert.enum";
import { IInstructions } from "../../automation/interfaces/i-instructions";
import { getCurrentDateTime } from "../../../utilities/date-time";
import { IWorkflowExecution, IWorkflowInstructionExecution, } from "../interfaces/i-automation";
import { IWorkflow } from "../../automation/interfaces/i-workflow";
import { ChatMessageService } from "./chat-message.service";
import { ChatRole } from "../../../enums/api.enum";
import { ExecutionStatusEnum, InstructionType, } from "../../../enums/automation.enum";
import { AutomationStateService } from "./automation-state.service";
import { UtilitiesService } from "../../../services/utilities.service";
import { IOpenAiModel } from "../../../interfaces/i-open-ai-model";
import { ConversationStarterService } from "./conversation-starter.service";
import { MultiSessionService } from "../../../services/multi-session.service";
import { UiLayoutService } from "../../../ui-layout/services/ui-layout.service";
import { SessionStatus } from "../../../enums/session-status.enum";

@Injectable({
  providedIn: "root",
})
export class AutomationExecutionService {
  #instructionExecutionSubscriptions = new Map<string | number, Subscription>();
  #profileService = inject(ProfileService);
  #apiService = inject(ApiService);
  #alertService = inject(AlertService);
  #chatSessionService = inject(ChatSessionService);
  #chatMessageService = inject(ChatMessageService);
  #automationStateService = inject(AutomationStateService);
  #utilitiesService = inject(UtilitiesService);
  readonly #conversationStarterService = inject(ConversationStarterService);
  readonly #multiSessionService = inject(MultiSessionService);
  readonly #uiLayoutService = inject(UiLayoutService);

  /**
   * Signal to queue dialog opening requests for user input.
   * Format: Array of { sessionId, inputType }
   */
  userInputNeeded = signal<Array<{ sessionId: string | number; inputType: InstructionType.CollectInformation | InstructionType.FileUpload }>>([]);

  #isActiveSession(sessionId: string | number): boolean {
    return this.#chatSessionService.selectedSessionId() === sessionId;
  }

  /**
   * Resolves the numeric sensitivity ranking for a stored label thin reference.
   * The legacy `id` field must not be used for ordering; resolve to the canonical
   * `sensitivity` level, falling back to any legacy `sensitivity` value, then 0.
   */
  #labelRank(label: ISensitivityLabel | undefined): number {
    if (!label) {
      return 0;
    }
    return mapLegacySensitivityLabel(label)?.sensitivity ?? label.sensitivity ?? 0;
  }

  #getSession(sessionId: string | number): IChatSession | undefined {
    return this.#chatSessionService.chatSessions()?.find((s) => s.id === sessionId);
  }

  #getMessagesForSession(sessionId: string | number): IMessage[] {
    if (this.#isActiveSession(sessionId)) {
      return this.#chatMessageService.messages() ?? [];
    }
    const session = this.#getSession(sessionId);
    return session?.messages ?? [];
  }

  #setMessagesForSession(sessionId: string | number, messages: IMessage[]): void {
    // Use update() instead of get+set to avoid race conditions with concurrent session updates
    this.#chatSessionService.chatSessions.update(sessions => {
      if (!sessions) {
        return sessions;
      }

      return sessions.map((s) =>
        s.id === sessionId ? { ...s, messages: [ ...messages ] } : s
      );
    });

    if (this.#isActiveSession(sessionId)) {
      this.#chatMessageService.messages.set([ ...messages ]);
    } else {
      this.#persistSessionById(sessionId);
    }
  }

  #addMessageToSession(
    sessionId: string | number,
    params: CreateMessageParams,
    doSanitize: boolean = true
  ): IMessage | undefined {
    let newMessage: IMessage | undefined;

    // Atomically read current messages, add new message, and update in one operation
    // This prevents race conditions when multiple sessions add messages concurrently
    this.#chatSessionService.chatSessions.update(sessions => {
      if (!sessions) {
        return sessions;
      }

      const session = sessions.find(s => s.id === sessionId);
      if (!session) {
        return sessions;
      }

      const existingMessages = session.messages ?? [];
      newMessage = this.#chatMessageService.createMessage(params, doSanitize);
      const updatedMessages = [...existingMessages, newMessage];

      return sessions.map((s) =>
        s.id === sessionId ? { ...s, messages: updatedMessages } : s
      );
    });

    // Update active session messages if this is the selected session
    if (this.#isActiveSession(sessionId)) {
      const session = this.#chatSessionService.chatSessions()?.find(s => s.id === sessionId);
      if (session?.messages) {
        this.#chatMessageService.messages.set([...session.messages]);
      }
    } else {
      this.#persistSessionById(sessionId);
    }

    return newMessage;
  }

  /**
   * Creates a new chat session for the provided workflow and then starts
   * execution of that workflow.
   *
   * \- Builds a minimal session "item" from the workflow details
   *   (name and sensitivity labels).
   * \- Delegates session creation to `createChatSession`, which
   *   persists the session and updates chat session state.
   * \- Once the session is created, initiates workflow execution
   *   via `startWorkflowExecution`.
   *
   * This is called when a workflow is selected from the UI and needs
   * to be run in the context of a new chat session.
   *
   * @param workflow The workflow whose details are used to create the
   *                 chat session and then execute.
   */
  async createWorkflowSession(workflow: IWorkflow): Promise<void> {
    if (this.#multiSessionService.hasReachedMaxConcurrency()) {
      this.#multiSessionService.showMaxConcurrencyAlert();
      return;
    }

    const item = {
      name: workflow.details.name,
      sensitivityLabels: workflow.details.sensitivityLabels
    };
    const id = await this.createChatSession(item, false);
    await this.#chatSessionService.refreshAndNavigateToSession(id, 'create');
    const canStartWorkflowExecution = await this.areInstructionModelsAvailable(workflow);
    if (!canStartWorkflowExecution) {
      return;
    }

    this.#multiSessionService.startSession(id);
    this.startWorkflowExecution(workflow, id);
  }

    /**
   * Creates a new chat session for the provided instruction and then starts
   * execution of that instruction.
   *
   * \- Builds a minimal session "item" from the instruction details
   *   (name and sensitivity labels).
   * \- Delegates session creation to `createChatSession`, which
   *   persists the session and updates chat session state.
   * \- Once the session is created, initiates instruction execution
   *   via `startInstructionExecution`.
   *
   * This is called when a instruction is selected from the UI and needs
   * to be run in the context of a new chat session.
   *
   * @param instruction The instruction whose details are used to create the
   *                    chat session and then execute.
   */
  async createInstructionSession(instruction: IInstructions): Promise<void> {
    if (this.#multiSessionService.hasReachedMaxConcurrency()) {
      this.#multiSessionService.showMaxConcurrencyAlert();
      return;
    }

    const item = {
      name: instruction.details.name,
      sensitivityLabels: instruction.details.sensitivityLabels!
    };
    const id = await this.createChatSession(item, false);
    await this.#chatSessionService.refreshAndNavigateToSession(id, 'create');
    this.#multiSessionService.startSession(id);
    this.startInstructionExecution();
  }

  /**
   * Generates and persists the configuration for a new chat session
   * based on either:
   *
   * \- The current `instructionInProgress` (instruction\-driven session), or
   * \- The conversation starter state from `ConversationStarterService`
   *   (free\-form prompt session).
   *
   * Behavior:
   * \- If there is no `instructionInProgress`, validates that the
   *   conversation has a non\-empty prompt; if not, it exits early.
   * \- Derives a session name from the prompt (trimmed and truncated)
   *   when not instruction\-driven.
   * \- Builds an appropriate session configuration object including
   *   sensitivity labels, agents, model, temperature, and `topP`.
   * \- Clears any existing messages in `ChatMessageService`.
   * \- Delegates to `createChatSession` to create and persist the
   *   new session and update session state.
   *
   * This method is typically invoked when a user submits a prompt
   * or selects a starter item that is not part of a workflow.
   *
   * Executed from the ai-chat-prompt component when user enters a prompt and hits enter or clicks send
   * Or executed from the starter-item component when user selects an instruction from the conversation starter
   */
  async generateChatSessionConfiguration(): Promise<boolean> {
    if (this.#multiSessionService.hasReachedMaxConcurrency()) {
      this.#multiSessionService.showMaxConcurrencyAlert();
      return false;
    }

    const instructionInProgress = this.#automationStateService.instructionInProgress();
    const conversation = this.#conversationStarterService.conversation();

    // Validate conversation prompt exists
    if (!instructionInProgress && (!conversation?.prompt || conversation.prompt.length === 0)) {
      return false;
    }

    // At this point, we know conversation and prompt exist
    const prompt = conversation?.prompt ?? '';
    const sessionName = prompt.length > 30 ? `${prompt.slice(0, 30)}...` : prompt;

    // Build session configuration
    const sessionConfig = instructionInProgress
      ? {
        name: instructionInProgress.details.name,
        sensitivityLabels: instructionInProgress.details.sensitivityLabels!,
        agents: conversation?.agents,
        model: instructionInProgress.details.model,
        temperature: instructionInProgress.details.temperature,
        topP: instructionInProgress.details.topP
      }
      : {
        name: sessionName,
        sensitivityLabels: [conversation!.sensitivityLabel],
        agents: conversation?.agents,
        model: conversation?.model,
        temperature: conversation?.temperature,
        topP: conversation?.topP
      };

    // Mark that the session was created from the conversation starter
    this.#conversationStarterService.isSessionCreatedFromStartConversation.set(true);

    this.#chatMessageService.messages.set([]);
    const id = await this.createChatSession(sessionConfig, false);

    // Handle user input instructions by setting session state BEFORE navigation
    // This ensures ai-chat component can detect the paused state when it loads
    // Only apply this logic if we're creating a session FROM an instruction (not a freeform prompt)
    if (instructionInProgress &&
        (this.#automationStateService.isWaitingForUserInput() || this.#automationStateService.isWaitingForFileUpload())) {
      const isWaitingForUserInput = instructionInProgress.details.type === InstructionType.CollectInformation;
      const isWaitingForFileUpload = instructionInProgress.details.type === InstructionType.FileUpload;

      if (isWaitingForUserInput || isWaitingForFileUpload) {
        const userInputType = isWaitingForUserInput
          ? InstructionType.CollectInformation
          : InstructionType.FileUpload;

        // Update session-specific state
        this.#automationStateService.updateSessionState(id, {
          isWaitingForUserInput,
          isWaitingForFileUpload,
          instructionInProgress
        });

        // Pause the session in MultiSessionService (updates icon and state)
        this.#multiSessionService.pauseSessionForUserInput(
          id,
          userInputType,
          undefined, // No workflowId for standalone instructions
          instructionInProgress.id,
          instructionInProgress
        );
      }
    }

    await this.#chatSessionService.refreshAndNavigateToSession(id, 'create');
    return true;
  }

  /**
   * Creates a new chat session based on the workflow/instruction execution.
   */
  async createChatSession(item: {
    name: string,
    sensitivityLabels: ISensitivityLabel[],
    agents?: string[],
    model?: IOpenAiModel,
    temperature?: number,
    topP?: number
  }, doRedirect = true): Promise<string> {
    const label = `${getCurrentDateTime(new Date())} ${item.name.slice(0, 30)}`;
    const defaultSession: any = JSON.parse(this.#profileService.userProfile()?.config?.defaultSession!);

    // Expand sensitivity labels to include children if parent is selected
    const expandedLabels = this.#utilitiesService.expandSensitivityLabelsWithChildren(item.sensitivityLabels);
    const lowestLabelExpanded = this.#utilitiesService.getLowestSensitivityLabel(expandedLabels);

    // Get the default config values from the API in order to create the chat session.
    const id = await firstValueFrom(
      this.#apiService
        .getDefaultConfigValues()
        .pipe(
          catchError((error) => {
            // The user is starting a workflow/instruction which initiates the process to create a chat session.  We need to get the
            // default config values in order to create the chat session.  We don't need to let the user know that there is
            // an error getting the default config values, just that the workflow/instruction failed to start.
            this.#alertService.showAlert({
              message: `Failed to initiate workflow/instruction.`,
              type: AlertEnum.error,
            });
            console.error(`Failed to get default config values.`, error);
            throw error;
          }),
          switchMap((defaultValues: unknown) => {
            const generatedId = crypto.randomUUID();
            const defaults = defaultValues as {
              sensitivityLabel: ISensitivityLabel;
              temperature: number;
              topP: number;
              agents: string[];
              model: IOpenAiModel;
            };

            const sensitivityLabel: ISensitivityLabel = lowestLabelExpanded
              ? toISensitivityLabel(lowestLabelExpanded)
              : defaults.sensitivityLabel;

            // if we don't have agents from item, filter the default agents based on the Session and Agent(s) SL
            const effectiveSL = lowestLabelExpanded ?? mapLegacySensitivityLabel(defaults.sensitivityLabel);
            const chatSessionAgents$ = item.agents
              ? of(item.agents)
              : effectiveSL
                ? this.#utilitiesService.filterAgentsBySensitivityLabel(defaults.agents, effectiveSL)
                : of(defaults.agents);

            return chatSessionAgents$.pipe(
              map((chatSessionAgents) => {
                const temperature = item.temperature ?? defaults.temperature;
                const topP = item.topP ?? defaults.topP;
                const requestBody: IChatSession = {
                  id: generatedId,
                  label,
                  version: defaultSession.version,
                  agents: chatSessionAgents,
                  temperature,
                  topP,
                  model: item.model ?? defaults.model,
                  isValid: true,
                  messages: [],
                  sensitivityLabels: [ sensitivityLabel ],
                  snapshotConfiguration: {
                    id: generatedId,
                    label,
                    agents: item.agents ?? defaults.agents,
                    temperature,
                    topP,
                    model: item.model ?? defaults.model,
                    sensitivityLabels: [ sensitivityLabel ],
                  },
                };
                return requestBody;
              })
            );
          }),
          switchMap((requestBody: IChatSession) =>
            // Create the chat session and start the workflow execution
            this.#apiService
              .persistSession(requestBody)
              .pipe(map((response) => ({ response, requestBody })))
          ),
          map(({ response, requestBody }) => {
            const action: ActionType = "create";

            // First explicitly set the selected session ID to ensure that subsequent operations use the correct session
            this.#chatSessionService.selectedSessionId.set(response.id);

            // Now update the selected sessions
            this.#chatSessionService.chatSessions.update((sessions) => {
              const sessionIndex = sessions?.findIndex(
                (session) => session.id === requestBody.id
              );
              if (sessionIndex !== undefined && sessionIndex !== -1) {
                sessions![sessionIndex] = requestBody;
              } else {
                sessions?.push(requestBody);
              }
              return sessions;
            });

            // for some reason, the computed() trigger doesn't seem to always be catching changes,
            // added the next few lines to make sure the update happens
            const chatSessions = this.#chatSessionService.chatSessions();
            if (chatSessions) {
              this.#chatSessionService.chatSessions.set(chatSessions);
            }

            if (doRedirect) {
              this.#chatSessionService.completeCreateOrEditSession(
                response.id,
                action
              );
            }

            return response.id;
          }),
          catchError((error: any) => {
            this.#alertService.showAlert({
              message: `Failed to create chat session.`,
              type: AlertEnum.error,
            });
            throw error;
          })
        )
    );

    return id;
  }

  /**
   * Initiates the execution of a workflow and sets up the chat session.
   *
   * - Sets the workflow as in progress and stores it in the execution state.
   * - Calls the API to start the workflow execution.
   * - On success, updates the workflow and instruction execution state, adds the initial instruction message,
   *   updates the chat session configuration, and persists the session.
   * - Handles errors by hiding the spinner and showing an alert.
   *
   * @param workflow The workflow object to be executed.
   * @param sessionId The chat session ID to associate workflow output with; if omitted, uses the currently selected session.
   */
  startWorkflowExecution(workflow: IWorkflow, sessionId: string | number) {
    // We do this multi session check here as well, just in case someone kicks off a
    // WF from the Automation Library.
    // We check to see if we reached max concurrency, if so we then check to see if the sessionId is in the
    // if the sessionId is not in the active sessions, then we show the alert and not proceed with anything.
    // TL;DR if we reached max concurrency AND the sessionId is not part of the active sessions, we show the alert and not run the WF.
    if (this.#multiSessionService.hasReachedMaxConcurrency()) {
      if (!this.#multiSessionService.isSessionStreaming(sessionId)) {
        this.#multiSessionService.showMaxConcurrencyAlert();
        return;
      }
    }

    // We check if the sessionId is NOT part of the active sessions,
    // if it isn't, we add it to the list of active sessions.
    // else, carry on normally.
    // we should only add it in this case:
    // 1. if we haven't reached max concurrency limit
    // 2. if a user kicked this WF from the Automation Library.
    // that is bc we don't do the adding of the session to the list from the createWorkflowSession(),
    // but we still need to add the session to the list of active sessions.
    if (!this.#multiSessionService.isSessionStreaming(sessionId)) {
      this.#multiSessionService.startSession(sessionId);
    }
    this.saveSessionSnapshotConfiguration();

    const currentSessionId = sessionId ?? this.#chatSessionService.selectedSessionId();
    this.#uiLayoutService.updateSessionStatusIcon(sessionId, SessionStatus.PROCESSING);
    this.#uiLayoutService.updateAndSortChatSessionsInLeftNav(String(sessionId), this.#uiLayoutService.navItems(), true);

    this.#apiService.startWorkflow(workflow.id!, currentSessionId).subscribe({
      next: (response) => {
        // Convert instruction date strings to Date objects before assignment
        const fixedResponse = {
          ...response,
          instruction: {
            ...response.instruction,
            createdDate: response.instruction.createdDate
              ? new Date(response.instruction.createdDate)
              : undefined,
            modifiedDate: response.instruction.modifiedDate
              ? new Date(response.instruction.modifiedDate)
              : undefined,
          },
        };
        this.#automationStateService.updateSessionState(currentSessionId, {
          currentInstructionIndex: 0,
          isWorkflowInProgress: true,
          workflowBeingExecuted: workflow,
          instructionInProgress: fixedResponse.instruction,
          workflowExecutionState: fixedResponse as IWorkflowExecution,
        });

        // First check if the instruction is a type InstructionType.CollectInformation or FileUpload
        const isTypeCollectInformation =
          fixedResponse.instruction.details.type ===
          InstructionType.CollectInformation;

        const isTypeFileUpload =
          fixedResponse.instruction.details.type ===
          InstructionType.FileUpload;

        if (isTypeCollectInformation || isTypeFileUpload) {
          // First instruction requires user input - pause the session
          const userInputType = isTypeCollectInformation
            ? InstructionType.CollectInformation
            : InstructionType.FileUpload;

          this.#automationStateService.updateSessionState(currentSessionId, {
            isWaitingForUserInput: isTypeCollectInformation,
            isWaitingForFileUpload: isTypeFileUpload,
            isWorkflowInProgress: false,
            currentInstructionIndex: 0,
          });

          // Pause the session in MultiSessionService
          this.#multiSessionService.pauseSessionForUserInput(
            currentSessionId,
            userInputType,
            fixedResponse.workflowExecutionId,
            fixedResponse.instruction.id,
            fixedResponse.instruction
          );

          // Add to queue to trigger dialog opening only if not already queued
          // This prevents duplicate dialog requests when navigating between routes
          this.userInputNeeded.update(queue => {
            const alreadyQueued = queue.some(item => item.sessionId === currentSessionId);
            return alreadyQueued ? queue : [...queue, { sessionId: currentSessionId, inputType: userInputType }];
          });
        } else {
          // Convert date strings to Date objects
          const instruction: IInstructions = {
            ...response.instruction,
            createdDate: response.instruction.createdDate
              ? new Date(response.instruction.createdDate)
              : undefined,
            modifiedDate: response.instruction.modifiedDate
              ? new Date(response.instruction.modifiedDate)
              : undefined,
          };

          // First create a chat session message with the instruction details
          const currentUserId = this.#profileService.userProfile()?.profile.name!;
          // if instruction type is "FileUpload", don't add a message to the chat session.
          if (instruction.details.type !== InstructionType.FileUpload) {
            this.#addMessageToSession(currentSessionId, {
              senderId: currentUserId,
              role: ChatRole.User,
              content: instruction.details.prompt,
              canSubmit: true,
              canCopy: true,
              currentUserId,
              id: instruction.id
            });
            if (this.#isActiveSession(currentSessionId)) {
              this.updateChatSessionConfiguration(instruction);
            }
          }

          // Update the chat session with the instruction details only for the active session;
          // non-active sessions are persisted via #addMessageToSession/#setMessagesForSession.
          if (this.#isActiveSession(currentSessionId)) {
            this.updateChatSession();
          }
          // Update session icon
          this.#uiLayoutService.updateSessionStatusIcon(sessionId, SessionStatus.PROCESSING);

          // Start polling for this workflow's instruction execution for the given session.
          this.startInstructionExecution(currentSessionId);
        }
      },
      error: (error: any) => {
        this.#multiSessionService.errorSession(sessionId);

        const errorMessages: Record<number, string> = {
          403: error?.error,
          400: error?.error?.error,
        };

        const message: string = errorMessages[error.status] ?? 'Failed to start the workflow.';
        this.#alertService.showAlert({
          message,
          type: AlertEnum.error,
        });
      },
    });
  }

  /**
   * Starts polling the workflow execution status at regular intervals.
   *
   * This method repeatedly requests the current workflow instruction execution status from the API.
   * It updates the instruction execution state and generates chat messages based on the response.
   * Polling stops automatically when the workflow is completed, cancelled, or failed.
   */
  startInstructionExecution(sessionId?: string | number): void {
    const effectiveSessionId = sessionId ?? this.#chatSessionService.selectedSessionId();
    if (!effectiveSessionId) {
      return;
    }

    // We do this multi session check here as well, just in case someone kicks off a
    // Instruction from the Automation Library.
    // We check to see if we reached max concurrency, if so we then check to see if the sessionId is in the
    // if the sessionId is not in the active sessions, then we show the alert and not proceed with anything.
    // TL;DR if we reached max concurrency AND the sessionId is not part of the active sessions, we show the alert and not run the Instruction.
    if (this.#multiSessionService.hasReachedMaxConcurrency()) {
      if (!this.#multiSessionService.isSessionStreaming(effectiveSessionId)) {
        this.#multiSessionService.showMaxConcurrencyAlert();
        return;
      }
    }

    // We check if the sessionId is NOT part of the active sessions,
    // if it isn't, we add it to the list of active sessions.
    // else, carry on normally.
    // we should only add it in this case:
    // 1. if we haven't reached max concurrency limit
    // 2. if a user kicked this Instruction from the Automation Library.
    // that is bc we don't do the adding of the session to the list from the createInstructionSession(),
    // but we still need to add the session to the list of active sessions.
    if (!this.#multiSessionService.isSessionStreaming(effectiveSessionId)) {
      this.#multiSessionService.startSession(effectiveSessionId);
    }

    // Update session icon
    this.#uiLayoutService.updateSessionStatusIcon(effectiveSessionId, SessionStatus.PROCESSING);
    this.#uiLayoutService.updateAndSortChatSessionsInLeftNav(String(effectiveSessionId), this.#uiLayoutService.navItems(), true);

    // Make sure to clean up any old subscription for this session before starting a new one
    this.#stopInstructionExecution(effectiveSessionId);

    // Use timer to emit values every 1000ms (1 second)
    const subscription = timer(0, 1000)
      .pipe(
        // For each timer tick, get the workflow execution status
        switchMap(() => {
          const sessionState = this.#automationStateService.getSessionState(effectiveSessionId);
          const workflowState = sessionState?.workflowExecutionState;
          const workflowDefinition = sessionState?.workflowBeingExecuted;

          if (!workflowState) {
            return EMPTY;
          }

          if (!workflowState.workflowExecutionId || workflowState.workflowExecutionId === 'random-id') {
            console.error('Invalid workflow execution ID:', workflowState.workflowExecutionId);
            return EMPTY;
          }

          const currentInstructionIndex = sessionState.currentInstructionIndex;

          if (currentInstructionIndex == null) {
            console.error('Missing currentInstructionIndex for session:', effectiveSessionId);
            return EMPTY;
          }

          return this.#apiService.getWorkflowExecutionStatus(
            workflowState.workflowExecutionId,
            currentInstructionIndex
          );
        }),
        // Use takeWhile to complete the observable when conditions are met.
        // NOTE: Treat a "success" workflowExecutionStatus as non-terminal/don't stop executing the WF
        // IF AND ONLY IF there is still a nextInstruction to run.
        // This is to fix the issue with running multiple WF's concurrently and not completing correctly.
        takeWhile((response: IWorkflowInstructionExecution) => {
          const status = response.workflowExecutionStatus;
          const hasNext = !!response.nextInstruction;

          return status === ExecutionStatusEnum.Waiting ||
            status === ExecutionStatusEnum.UserInput ||
            status === ExecutionStatusEnum.InProgress ||
            (status === ExecutionStatusEnum.Success && hasNext);
        }, true)
      )
      .subscribe({
        next: (response) => {
          this.#automationStateService.updateSessionState(effectiveSessionId, {
            instructionsExecutionState: response,
          });

          const isWorkflowCancelledOrFailed =
            response.workflowExecutionStatus === ExecutionStatusEnum.Cancelled ||
            response.workflowExecutionStatus === ExecutionStatusEnum.Failed;

          const isAwaitingUserInput =
            response.workflowExecutionStatus === ExecutionStatusEnum.UserInput;

          const isInstructionSuccessAndNoNext =
            response.status === ExecutionStatusEnum.Success &&
            response.nextInstruction == null;

          if (isAwaitingUserInput) {
            let isWaitingForUserInput = false;
            let isWaitingForFileUpload = false;
            let shouldActuallyPause = false;

            // Determine the instruction type that needs user input
            // Priority: current instruction type > next instruction type
            let instructionTypeNeedingInput: InstructionType | null = null;

            if (response.status === ExecutionStatusEnum.UserInput && response.instructionType) {
              instructionTypeNeedingInput = response.instructionType;
            } else if (response.nextInstruction?.details?.type) {
              instructionTypeNeedingInput = response.nextInstruction.details.type;
            }

            // Set the appropriate waiting flags based on instruction type
            if (instructionTypeNeedingInput === InstructionType.CollectInformation) {
              isWaitingForUserInput = true;
              shouldActuallyPause = true;
            } else if (instructionTypeNeedingInput === InstructionType.FileUpload) {
              isWaitingForFileUpload = true;
              shouldActuallyPause = true;
            }

            // Update AutomationStateService
            this.#automationStateService.updateSessionState(effectiveSessionId, {
              isWaitingForUserInput,
              isWaitingForFileUpload,
              isWorkflowInProgress: !shouldActuallyPause,
            });

            // Generate messages for current instruction
            this.#generateMessages(response, effectiveSessionId);

            // Only stop polling if we're actually pausing at a user input instruction
            if (shouldActuallyPause) {
              this.#stopInstructionExecution(effectiveSessionId);
            }
          } else {
            this.#generateMessages(response, effectiveSessionId);
          }


          // If a terminal condition is met, stop polling
          // NOTE: removed the #generateMessages() below since it was already called above, so don't call it again
          // to avoid double-processing (which would cause completeSession to be called twice)
          if (isInstructionSuccessAndNoNext || isWorkflowCancelledOrFailed) {
            this.#stopInstructionExecution(effectiveSessionId);
          }
        },
        error: (error) => {
          this.#multiSessionService.errorSession(effectiveSessionId);
          this.endWorkflowExecution(undefined, effectiveSessionId); // Stop the timer and clean up on error
          // Handle error but don't manipulate workflow state as takeWhile now handles subscription completion
          this.#alertService.showAlert({
            message: `Error fetching workflow status.`,
            type: AlertEnum.error,
          });
        },
        complete: () => {
        }
      });

      this.#instructionExecutionSubscriptions.set(effectiveSessionId, subscription);
  }

  /**
   * Continues workflow execution by submitting user input to the current workflow execution.
   *
   * - Resets waiting and in-progress workflow states.
   * - Validates the existence of a workflow execution ID.
   * - Calls the API to continue the workflow with the provided user input.
   * - Handles API errors by updating workflow state and showing an alert.
   *
   * @param userInput The user's input to be submitted for the current workflow instruction.
   */
  startInstructionExecutionWithUserInput(userInput: string, sessionId?: string | number): void {
    const effectiveSessionId = sessionId ?? this.#chatSessionService.selectedSessionId();
    if (!effectiveSessionId) {
      return;
    }

    this.#automationStateService.updateSessionState(effectiveSessionId, {
      isWorkflowInProgress: true,
    });
    const workflowExecutionId =
      this.#automationStateService.getSessionState(effectiveSessionId)?.workflowExecutionState
        ?.workflowExecutionId;

    if (!workflowExecutionId) {
      this.#alertService.showAlert({
        message: `Could not start instruction. No workflow execution ID available.`,
        type: AlertEnum.error,
      });
      return;
    }

    this.#apiService
      .startWorkflowWithUserInput(workflowExecutionId, userInput)
      .subscribe({
        next: (response) => {
          if (
            !this.#automationStateService.getSessionState(effectiveSessionId)?.isWaitingForUserInput &&
            !this.#automationStateService.getSessionState(effectiveSessionId)?.isWaitingForFileUpload &&
            this.#automationStateService.getSessionState(effectiveSessionId)?.isWorkflowInProgress
          ) {
            this.startInstructionExecution(effectiveSessionId);
          }
        },
        error: (error: any) => {
          this.#multiSessionService.errorSession(effectiveSessionId);
          this.#automationStateService.updateSessionState(effectiveSessionId, {
            isWorkflowInProgress: false,
          });
          this.#alertService.showAlert({
            message: `Failed to start the workflow with your response.`,
            type: AlertEnum.error,
          });
        },
      });
  }

  /**
   * Continues workflow execution by submitting user input with the file attachments to the current workflow execution.
   *
   * - Resets waiting and in-progress workflow states.
   * - Validates the existence of a workflow execution ID.
   * - Calls the API to continue the workflow with the provided user file attachments.
   * - Handles API errors by updating workflow state and showing an alert.
   */
  startInstructionExecutionWithFileAttachments(messages: IMessage[], sessionId?: string | number): void {
    const effectiveSessionId = sessionId ?? this.#chatSessionService.selectedSessionId();
    if (!effectiveSessionId) {
      return;
    }

    this.#automationStateService.updateSessionState(effectiveSessionId, {
      isWorkflowInProgress: true,
    });
    const workflowExecutionId = this.#automationStateService.getSessionState(effectiveSessionId)?.workflowExecutionState?.workflowExecutionId;

    if (!workflowExecutionId) {
      this.#alertService.showAlert({
        message: `Could not start instruction. No workflow execution ID available.`,
        type: AlertEnum.error,
      });
      return;
    }

    // CALL API TO CONTINUE WORKFLOW WITH FILE ATTACHMENTS
    let messagesInput: IMessage[] = [ ...messages ];
    this.#apiService
      .startWorkflowWithFileUploadInput(workflowExecutionId, messagesInput)
      .subscribe({
        next: (response) => {
          if (
            !this.#automationStateService.getSessionState(effectiveSessionId)?.isWaitingForUserInput &&
            !this.#automationStateService.getSessionState(effectiveSessionId)?.isWaitingForFileUpload &&
            this.#automationStateService.getSessionState(effectiveSessionId)?.isWorkflowInProgress
          ) {
            this.startInstructionExecution(effectiveSessionId);
          }
        },
        error: (error: any) => {
          this.#multiSessionService.errorSession(effectiveSessionId);
          this.#automationStateService.updateSessionState(effectiveSessionId, {
            isWorkflowInProgress: false,
          });
          this.#alertService.showAlert({
            message: `Failed to start the workflow with your file attachments.`,
            type: AlertEnum.error,
          });
        },
      });
  }

  /**
   * Handles user input to continue the workflow instruction execution.
   *
   * This is for the instruction type InstructionType.CollectInformation.
   * - Adds the user's response as a chat message.
   * - Resets workflow progress and waiting states.
   * - Updates the chat session with the new message.
   * - Calls the API to continue workflow execution with the provided user input.
   *
   * @param userInput The user's input to be submitted for the current workflow instruction.
   */
  continueInstructionExecutionWithUserInput(userInput: string, sessionId?: string | number): void {
    const effectiveSessionId = sessionId ?? this.#chatSessionService.selectedSessionId();
    if (!effectiveSessionId) {
      return;
    }

    const instruction = this.#automationStateService.getSessionState(effectiveSessionId)?.instructionInProgress ?? this.#multiSessionService.getSessionInstructionInProgress(effectiveSessionId);
    if (!instruction) {
      console.error('No instruction in progress for session:', effectiveSessionId);
      return;
    }

    this.#automationStateService.updateSessionState(effectiveSessionId, {
      isWorkflowInProgress: false,
      isWaitingForUserInput: false,
      isWaitingForFileUpload: false,
    });
    const currentUserId = this.#profileService.userProfile()?.profile.name!;

    // Always add user input message (don't deduplicate by instruction ID)
    // This is consistent with AI prompts - we want to see user inputs for every workflow run.
    // Use a unique ID by appending timestamp to avoid any ID conflicts
    const userInputMessageId = `${instruction.id}-userinput-${Date.now()}`;
    this.#addMessageToSession(effectiveSessionId, {
      senderId: currentUserId,
      role: ChatRole.User,
      content: `${instruction.details.prompt} ${userInput}`,
      canSubmit: true,
      canCopy: true,
      currentUserId,
      id: userInputMessageId,
    });

    // Add a "Waiting" message to the chat session messages.
    this.#addMessageToSession(effectiveSessionId, {
      senderId: ChatRole.Assistant,
      role: ChatRole.Assistant,
      content: "Processing...",
      canSubmit: false,
      canCopy: false,
      currentUserId,
      id: ExecutionStatusEnum.Waiting,
    });

    // Always update/persist the chat session after adding user input
    // For active sessions, update via updateChatSession
    // For non-active sessions, persistence happens in #addMessageToSession via #persistSessionById
    if (this.#isActiveSession(effectiveSessionId)) {
      this.updateChatSession();
    }
    // Note: Non-active sessions are already persisted via #addMessageToSession -> #persistSessionById

    this.startInstructionExecutionWithUserInput(userInput, effectiveSessionId);
  }

  /**
   * Continues workflow execution by submitting file attachments for the current instruction.
   *
   * - Resets workflow progress and waiting states.
   * - Updates the chat session.
   * - Submits the latest message with file attachments to the API.
   */
  async continueInstructionExecutionWithFileAttachments(sessionId?: string | number): Promise<void> {
    const effectiveSessionId = sessionId ?? this.#chatSessionService.selectedSessionId();
    if (!effectiveSessionId) {
      return;
    }

    this.#automationStateService.updateSessionState(effectiveSessionId, {
      isWorkflowInProgress: false,
      isWaitingForFileUpload: false,
      isWaitingForUserInput: false,
    });

    const messages = this.#getMessagesForSession(effectiveSessionId);
    this.startInstructionExecutionWithFileAttachments(messages, effectiveSessionId);
  }

  /**
   * Updates the current chat session based on the provided instruction.
   *
   * This method updates the chat session with new parameters like temperature, topP, model, and Sensitivity Labels
   * if the instructions are different from the current session's values.
   *
   * Adds the instruction's details to the chat session messages and persists the updated session.
   */
  async updateChatSession(sessionId?: string | number, skipRefreshFromBackend: boolean = false) {
    const effectiveSessionId = sessionId ?? this.#chatSessionService.selectedSessionId();

    const chatSession = effectiveSessionId === this.#chatSessionService.selectedSessionId()
      ? this.#chatSessionService.selectedSession()
      : this.#chatSessionService.chatSessions()?.find(s => s.id === effectiveSessionId);

    if (!chatSession) {
      return;
    }

    // Get messages from the correct session - use chatMessageService.messages() only for selected session
    const sessionMessages = effectiveSessionId === this.#chatSessionService.selectedSessionId()
      ? this.#chatMessageService.messages()!
      : chatSession.messages || [];

    let requestBody: IChatSession = {
      id: chatSession?.id!,
      label: chatSession?.label!,
      version: chatSession?.version!,
      agents: chatSession?.agents!,
      temperature: chatSession?.temperature!,
      topP: chatSession?.topP!,
      model: chatSession?.model,
      isValid: chatSession?.isValid,
      messages: sessionMessages,
      sensitivityLabels: chatSession?.sensitivityLabels,
      snapshotConfiguration: chatSession?.snapshotConfiguration || null,
      lastStatus: chatSession?.lastStatus,
    };

    try {
      // Now save the updated chat session with the instruction details in the messages
      await firstValueFrom(this.#apiService.persistSession(requestBody));

      const action: ActionType = "edit";
      // Now update the selected session.  In order to do this, we need to update the chat session because selectedSession
      // is a computed() that depends on the chatSessions signal.
      this.#chatSessionService.chatSessions.update((sessions) => {
        const sessionIndex = sessions?.findIndex(
          (session) => session.id === requestBody.id
        );
        if (sessionIndex !== undefined && sessionIndex !== -1) {
          sessions![sessionIndex] = requestBody;
        } else {
          sessions?.push(requestBody);
        }
        return sessions;
      });
      // for some reason, the computed() trigger doesn't seem to always be catching changes,
      // added the next few lines to make sure the update happens
      const chatSessions = this.#chatSessionService.chatSessions();
      if (chatSessions) {
        this.#chatSessionService.chatSessions.set(chatSessions);
      }

      const hasInstructionState = !!this.#automationStateService.getSessionState(chatSession.id)?.instructionsExecutionState;
      if (!hasInstructionState && !skipRefreshFromBackend) {
        // Only execute this if there is no existing instruction execution state.
        // This will create a new chat session navigation item in the left nav.
        // Pass moveToTop=false to prevent reordering the session during workflow execution
        this.#chatSessionService.completeCreateOrEditSession(
          requestBody.id,
          action,
          false
        );
      }
      if (
        !this.#automationStateService.getSessionState(chatSession.id)?.isWaitingForUserInput &&
        !this.#automationStateService.getSessionState(chatSession.id)?.isWaitingForFileUpload &&
        this.#automationStateService.getSessionState(chatSession.id)?.isWorkflowInProgress
      ) {
        this.startInstructionExecution(chatSession.id);
      }
    } catch (error) {
      this.#alertService.showAlert({
        message: `Failed to save the chat session.`,
        type: AlertEnum.error,
      });
    }
  }

  /**
   * Resets all workflow execution-related states to their initial values.
   *
   * This method is called when the workflow execution is completed, cancelled, or failed.
   * It clears the workflow execution state, instruction execution state, progress flags,
   * and any references to the current instruction or workflow being executed.
   */
  endWorkflowExecution(forceStop?: boolean, sessionId?: string | number, explicitStatus?: ExecutionStatusEnum): void {
    const effectiveSessionId = sessionId ?? this.#chatSessionService.selectedSessionId();
    if (!effectiveSessionId) {
      return;
    }

    // Stop the polling subscription for this session
    this.#stopInstructionExecution(effectiveSessionId);
    let sessionState = this.#automationStateService.getSessionState(effectiveSessionId);

    if (forceStop && sessionState?.workflowExecutionState && sessionState.instructionInProgress) {
      this.#forceStopWorkflowExecution(
        sessionState.workflowExecutionState,
        sessionState.instructionInProgress,
        effectiveSessionId
      );
      // Update with the latest values from the #forceStopWorkflowExecution() call.
      sessionState = this.#automationStateService.getSessionState(effectiveSessionId);
    }

    const workflowState = sessionState?.instructionsExecutionState?.workflowExecutionStatus;

    // The workflow execution has completed. Clear state for this session.
    this.#automationStateService.clearSessionState(effectiveSessionId);

    // Determine final status for the session icon
    // Priority: Explicit status > Failed/Cancelled status > explicit success (forceStop=false) > other workflow states > default Success
    let finalStatus: ExecutionStatusEnum;
    if (explicitStatus !== undefined) {
      // Use the explicitly provided status (e.g., from #generateFailedMessage)
      finalStatus = explicitStatus;
    } else if (workflowState === ExecutionStatusEnum.Failed || workflowState === ExecutionStatusEnum.Cancelled) {
      finalStatus = workflowState;
    } else if (forceStop === false) {
      // Explicitly completed successfully (called from handleNextInstruction when no more instructions)
      finalStatus = ExecutionStatusEnum.Success;
    } else if (workflowState) {
      // Use the workflow state if available
      finalStatus = workflowState;
    } else {
      // Default to Cancelled if no clear status.
      // This will update the Session icon to be the Default icon.
      finalStatus = ExecutionStatusEnum.Cancelled;
    }

    this.#multiSessionService.completeSession(effectiveSessionId, finalStatus);
  }

  /**
   * Updates the chat session configuration based on the provided instruction.
   *
   * If the instruction's details differ from the current chat session's parameters (temperature, topP, model), then
   * update the chat session with the new parameters and add a system message indicating the changes made.
   * @param instruction Optional instruction to use for configuration
   * @param isErrorRestore If true, indicates this is an error-triggered restoration (defaults to false)
   */
  async updateChatSessionConfiguration(
    instruction?: IInstructions,
    isErrorRestore: boolean = false
  ): Promise<void> {
    const chatSession = this.#chatSessionService.selectedSession();
    if (!chatSession) return;
    const currentUserId = this.#profileService.userProfile()?.profile.name!;

    const updateSession = (updatedSession: IChatSession) => {
      this.#chatSessionService.chatSessions.update((sessions) => {
        const idx = sessions?.findIndex((s) => s.id === updatedSession.id);
        if (idx !== undefined && idx !== -1) sessions![idx] = updatedSession;
        else sessions?.push(updatedSession);
        return sessions;
      });
    };
    if (instruction) {
      if (this.#doEditChatSession(instruction)) {
        const sessionConfigUpdates = await this.getSessionConfigurationUpdates(
          chatSession,
          instruction
        );
        const changes = sessionConfigUpdates.join("\n");

        if (changes) {
          if (!this.#checkIfInstructionIdExists(instruction.id, chatSession.id)) {
            this.#addMessageToSession(chatSession.id, {
              senderId: ChatRole.System,
              role: ChatRole.System,
              content: `Configuration Updated by: ${instruction.details.name} \n\n ${changes}`,
              canSubmit: true,
              canCopy: true,
              currentUserId,
              system: true,
              showExpansionPanel: true,
              expansionPanelTitle: "Session Configuration Updated",
              id: instruction.id,
            });
          }
        }
        const agents = instruction.details.agent
          ? chatSession.agents.includes(instruction.details.agent)
            ? [ ...chatSession.agents ]
            : [ ...chatSession.agents, instruction.details.agent ]
          : chatSession.agents;

        let chatSessionSensitivityLabel = chatSession.sensitivityLabels![0];
        let isInstructionSensitivityLabelHigher = false;
        if (instruction.details.type === InstructionType.Agent) {
          isInstructionSensitivityLabelHigher = this.#labelRank(instruction.details.sensitivityLabels?.[0]) > this.#labelRank(chatSession.sensitivityLabels?.[0]);

          if (isInstructionSensitivityLabelHigher) {
            chatSessionSensitivityLabel = instruction.details.sensitivityLabels![0];
          }
        }

        updateSession({
          ...chatSession,
          agents,
          sensitivityLabels: [ chatSessionSensitivityLabel! ],
          temperature: instruction.details.temperature,
          topP: instruction.details.topP,
          model: instruction.details.model,
          messages: this.#chatMessageService.messages()!,
          snapshotConfiguration: {
            ...chatSession,
            agents,
            id: chatSession.snapshotConfiguration?.id ?? chatSession.id ?? '',
            label: chatSession.snapshotConfiguration?.label ?? chatSession.label! ?? '',
            temperature: chatSession.snapshotConfiguration?.temperature ?? chatSession.temperature!,
            topP: chatSession.snapshotConfiguration?.topP! ?? chatSession.topP!,
            model: chatSession.snapshotConfiguration?.model ?? chatSession.model!,
            sensitivityLabels: [ chatSessionSensitivityLabel! ]
          }
        });

        // for some reason, the computed() trigger doesn't seem to always be catching changes,
        // added the next few lines to make sure the update happens
        // NOTE using a const here for the chatSessions so we get the latest version of it.
        // that's why we are not calling it at top of this method.
        // Force array reference change
        const chatSessions = this.#chatSessionService.chatSessions();
        if (chatSessions) {
          // Replace the session in a new array
          const updatedSessions = chatSessions.map((s) =>
            s.id === chatSession.id
              ? {
                ...s,
                // ...instruction.details,
                messages: this.#chatMessageService.messages()!,
              }
              : s
          );
          this.#chatSessionService.chatSessions.set(updatedSessions);
        }
        // NOTE: In order for selectedSession() to be updated, we need to set the selectedSessionId along with the chatSessions.
        // This is because selectedSession() is a computed() that depends on both the chatSessions signal and the selectedSessionId signal.
        this.#chatSessionService.selectedSessionId.set(chatSession.id);
        if (isInstructionSensitivityLabelHigher) {
          this.saveSessionSnapshotConfiguration();
        }
      }
    } else if (chatSession.snapshotConfiguration) {
      const instructionInProgress =
        this.#automationStateService.instructionInProgress();
      const instructionsExecutionState =
        this.#automationStateService.instructionsExecutionState();

      if (
        instructionInProgress &&
        !instructionsExecutionState?.nextInstruction
      ) {
        updateSession({
          ...chatSession.snapshotConfiguration,
          version: chatSession.version!,
          messages: this.#chatMessageService.messages()!,
          isValid: true,
          topP: chatSession.snapshotConfiguration.topP,
          temperature: chatSession.snapshotConfiguration.temperature,
          model: chatSession.snapshotConfiguration.model,
          agents: chatSession.snapshotConfiguration.agents,
          snapshotConfiguration: undefined,
        });
        // for some reason, the computed() trigger doesn't seem to always be catching changes,
        // added the next few lines to make sure the update happens
        // NOTE using a const here for the chatSessions so we get the latest version of it.
        // that's why we are not calling it at top of this method.
        // Force array reference change
        const chatSessions = this.#chatSessionService.chatSessions();
        if (chatSessions) {
          // Replace the session in a new array
          const updatedSessions = chatSessions.map((s) =>
            s.id === chatSession.id
              ? { ...s, messages: this.#chatMessageService.messages()! }
              : s
          );
          this.#chatSessionService.chatSessions.set(updatedSessions);
        }
        // NOTE: In order for selectedSession() to be updated, we need to set the selectedSessionId along with the chatSessions.
        // This is because selectedSession() is a computed() that depends on both the chatSessions signal and the selectedSessionId signal.
        this.#chatSessionService.selectedSessionId.set(chatSession.id);

        const sessionConfigUpdates = await this.getSessionConfigurationUpdates(
          chatSession,
          undefined,
          chatSession.snapshotConfiguration
        );
        const changes = sessionConfigUpdates.join("\n");

        // Set the name of who/what restored the configuration
        const configRestoredBy = isErrorRestore
          ? "System"
          : instructionInProgress?.details.name;
        if (changes) {
          if (
            !this.#checkIfInstructionIdExists(
              `${instructionInProgress!.id}_${ChatRole.System}`,
              chatSession.id
            )
          ) {
            this.#addMessageToSession(chatSession.id, {
              senderId: ChatRole.System,
              role: ChatRole.System,
              content: `Configuration Restored by: ${configRestoredBy} \n${changes}`,
              canSubmit: true,
              canCopy: true,
              currentUserId,
              system: true,
              showExpansionPanel: true,
              expansionPanelTitle: "Session Configuration Restored",
              id: `${instructionInProgress!.id}_${ChatRole.System}`,
            });
          }

          // Update the sessions again to include the newly added system message
          const updatedMessages = this.#chatMessageService.messages()!;
          const chatSessions = this.#chatSessionService.chatSessions();
          if (chatSessions) {
            // Replace the session in a new array with the updated messages
            const updatedSessionsWithMessage = chatSessions.map((s) =>
              s.id === chatSession.id ? { ...s, messages: updatedMessages } : s
            );
            this.#chatSessionService.chatSessions.set(
              updatedSessionsWithMessage
            );
          }
        }
      }
    }
  }

  /**
   * Creates a snapshot configuration from the current chat session configuration.
   * This allows the session to be restored to this state later.
   */
  saveSessionSnapshotConfiguration(): void {
    const chatSession = this.#chatSessionService.selectedSession();
    if (!chatSession) return;
    // Create a saved configuration from the current session
    const snapshotConfiguration: IChatSessionConfiguration = {
      id: chatSession.id,
      label: chatSession.label,
      agents: chatSession.agents ? [ ...chatSession.agents ] : [],
      temperature: chatSession.temperature,
      topP: chatSession.topP,
      model: chatSession.model,
      sensitivityLabels: chatSession.sensitivityLabels
    };

    // Update the chat session with the saved configuration
    const updatedSession: IChatSession = {
      ...chatSession,
      snapshotConfiguration,
    };

    chatSession.snapshotConfiguration = snapshotConfiguration;

    // Update the chat sessions signal
    this.#chatSessionService.chatSessions.update((sessions) => {
      if (!sessions) return sessions;

      const sessionIndex = sessions.findIndex(
        (session) => session.id === chatSession.id
      );
      if (sessionIndex !== -1) {
        sessions[sessionIndex] = updatedSession;
      }
      return sessions;
    });

    // NOTE: In order for selectedSession() to be updated, we need to set the selectedSessionId along with the chatSessions.
    // This is because selectedSession() is a computed() that depends on both the chatSessions signal and the selectedSessionId signal.
    this.#chatSessionService.selectedSessionId.set(chatSession.id);
  }

  /**
   * Compares the values between a chat session and instruction, returning an array of strings
   * describing the configuration changes.
   *
   * @param chatSession The current chat session
   * @param instruction The instruction with potential new configuration values
   * @param savedChatSessionConfiguration
   * @returns An array of strings describing the configuration changes
   */
  async getSessionConfigurationUpdates(
    chatSession: IChatSession,
    instruction?: IInstructions,
    savedChatSessionConfiguration?: IChatSessionConfiguration
  ): Promise<string[]> {
    const sessionConfigUpdates: string[] = [];

    if (instruction) {
      // Check for agent change if instruction type is 'Agent'
      if (instruction.details.type === InstructionType.Agent) {
        const previousAgents = chatSession.agents?.join(", ") || "";
        const newAgent = instruction.details.agent || "";
        if (!previousAgents.split(",").map(a => a.trim()).includes(newAgent)) {
          // newAgent is NOT in previousAgents
          const allAgentIds = [
            ...new Set([ ...chatSession.agents, instruction.details.agent! ]),
          ];

          const agents = await this.#getAgentNames(allAgentIds);

          // Get names for current agents
          const currentAgentNames = chatSession.agents.map(
            (id) => agents.get(id) || "Unknown"
          );

          // Get names for new agents
          const newAgentName =
            agents.get(instruction.details.agent!) || "Unknown";

          // Format agent names as comma-separated lists
          const currentAgentsFormatted =
            chatSession.agents && chatSession.agents.length > 0
              ? currentAgentNames.join(", ")
              : "None";

          if (instruction.details.sensitivityLabels) {
            // Find if instruction label id is greater than any chatSession label id
            const instructionLabel = instruction.details.sensitivityLabels[0];
            const instructionRank = this.#labelRank(instructionLabel);
            const isHigherLabel = chatSession.sensitivityLabels?.some(
              (label) => instructionRank > this.#labelRank(label)
            );

            const sensitivityLabelName = isHigherLabel ? mapLegacySensitivityLabel(instructionLabel)?.name : undefined;

            if (sensitivityLabelName) {
              sessionConfigUpdates.push(
                `- Sensitivity Label: ${mapLegacySensitivityLabel(chatSession.sensitivityLabels?.[0])?.name ?? ''} to ${sensitivityLabelName}`
              );
            }
          }
          if (previousAgents !== newAgent) {
            sessionConfigUpdates.push(
              `- Agent(s): added to session '${newAgentName}'`
            );
          }
        }
      }

      // Check for model change
      if (chatSession.model?.model !== instruction.details.model.model) {
        sessionConfigUpdates.push(
          `- Model: ${chatSession.model?.model} to ${instruction.details.model.model}`
        );
      }

      // Check for temperature change
      if (chatSession.temperature !== instruction.details.temperature) {
        sessionConfigUpdates.push(
          `- Temperature: ${chatSession.temperature} to ${instruction.details.temperature}`
        );
      }

      // Check for topP change
      if (chatSession.topP !== instruction.details.topP) {
        sessionConfigUpdates.push(
          `- TopP: ${chatSession.topP} to ${instruction.details.topP}`
        );
      }
    } else if (savedChatSessionConfiguration) {
      // If no instruction is provided, compare with saved configuration
      if (
        chatSession.agents?.join(", ") !==
        savedChatSessionConfiguration.agents?.join(", ")
      ) {
        // sessionConfigUpdates.push(`- Agent(s): ${chatSession.agents?.join(', ')} to ${savedChatSessionConfiguration.agents?.join(', ')}`);
        const previousAgents = chatSession.agents?.join(", ") || "";
        const newAgent = savedChatSessionConfiguration.agents?.join(", ") || "";
        const allAgentIds = [
          ...new Set([
            ...chatSession.agents,
            ...savedChatSessionConfiguration.agents!,
          ]),
        ];

        const agents = await this.#getAgentNames(allAgentIds);

        // Get names for the saved configuration agents
        const savedChatSessionConfigurationAgentNames =
          savedChatSessionConfiguration.agents?.map(
            (id) => agents.get(id) || "Unknown"
          );

        // Format agent names as comma-separated lists
        const savedAgentNamesFormatted =
          savedChatSessionConfigurationAgentNames &&
          savedChatSessionConfigurationAgentNames.length > 0
            ? savedChatSessionConfigurationAgentNames.join(", ")
            : "None";

        // Get names for chat session agents
        const chatSessionAgents = chatSession.agents.map(
          (id) => agents.get(id) || "Unknown"
        );

        // Format agent names as comma-separated lists
        const currentAgentsFormatted =
          chatSession.agents && chatSession.agents.length > 0
            ? chatSessionAgents.join(", ")
            : "None";

        if (previousAgents !== newAgent) {
          sessionConfigUpdates.push(
            `- Agent(s): added to session '${savedAgentNamesFormatted}'`
          );
        }
      }

      if (
        chatSession.model?.model !== savedChatSessionConfiguration.model?.model
      ) {
        sessionConfigUpdates.push(
          `- Model: ${chatSession.model?.model} to ${savedChatSessionConfiguration.model?.model}`
        );
      }

      if (
        chatSession.temperature !== savedChatSessionConfiguration.temperature
      ) {
        sessionConfigUpdates.push(
          `- Temperature: ${chatSession.temperature} to ${savedChatSessionConfiguration.temperature}`
        );
      }

      if (chatSession.topP !== savedChatSessionConfiguration.topP) {
        sessionConfigUpdates.push(
          `- TopP: ${chatSession.topP} to ${savedChatSessionConfiguration.topP}`
        );
      }
    }

    return sessionConfigUpdates;
  }

  /**
   * Determines if the chat session needs to be edited based on the instruction details.
   * @param instruction
   */
  #doEditChatSession(instruction: IInstructions): boolean {
    const chatSession = this.#chatSessionService.selectedSession();
    if (!chatSession) return false;

    const { model, temperature, topP } = instruction.details;

    // Check if the agent, model, temperature, or topP values are different from the current chat session
    if (instruction.details.type === "Agent") {
      const isAgentDifferent =
        chatSession.agents?.join(", ") !== instruction.details.agent;

      if (isAgentDifferent) {
        return true;
      }
    }

    const isModelDifferent = chatSession.model?.model !== model.model;
    const isTemperatureDifferent = chatSession.temperature !== temperature;
    const isTopPDifferent = chatSession.topP !== topP;

    return isModelDifferent || isTemperatureDifferent || isTopPDifferent;
  }

  /**
   * Fetches agent names for a list of agent IDs
   * @param agentIds List of agent IDs to fetch names for
   * @returns A Map of agent IDs to their names
   */
  async #getAgentNames(agentIds: string[]): Promise<Map<string, string>> {
    try {
      const agents = await firstValueFrom(
        this.#apiService.getAgentsByIds(agentIds)
      );

      // Map agent IDs to their names
      return new Map(
        agents.map((agent: any) => [ agent.id, agent.name || "Unknown" ])
      );
    } catch (err) {
      console.error("Error fetching agent names:", err);
      // Return a map with unknown names if API call fails
      return new Map(agentIds.map((id) => [ id, id ]));
    }
  }

  /**
   * Generates and updates chat messages based on the current instruction execution status.
   *
   * - If the status is `Waiting`, adds the instruction prompt and a "Processing..." message if they do not already exist.
   * - If the status is `Success`, removes the "Processing..." message, adds the AI response, and handles the next instruction or workflow completion.
   * - Updates the chat session when new messages are added.
   *
   * @param instruction The current workflow instruction execution object containing status, prompt, output, and next instruction details.
   * @param sessionId The session whose messages should be updated.
   */
  #generateMessages(instruction: IWorkflowInstructionExecution, sessionId: string | number): void {
    const currentUserId = this.#profileService.userProfile()?.profile.name!;
    const currentInstructionStatus = instruction.status;
    const messages = this.#getMessagesForSession(sessionId);

    if (currentInstructionStatus === ExecutionStatusEnum.Waiting) {
      this.#generateWaitingMessage(instruction, currentUserId, messages, sessionId);
    }

    if (currentInstructionStatus === ExecutionStatusEnum.Success) {
      this.#generateSuccessMessage(instruction, currentUserId, sessionId);
    }

    if (currentInstructionStatus === ExecutionStatusEnum.Failed) {
      this.#generateFailedMessage(instruction, currentUserId, sessionId);
    }
  }

  /**
   * Forces the workflow execution to stop and updates the chat session.
   *
   * - Removes the "Waiting" message.
   * - Adds a system message indicating the workflow was stopped by the user.
   * - Optionally calls the API to cancel the workflow.
   * - Persists the updated chat session.
   *
   * @param workflowExecutionState The current workflow execution state.
   * @param instructionInProgress The instruction currently in progress.
   */
  #forceStopWorkflowExecution(
    workflowExecutionState: IWorkflowExecution,
    instructionInProgress: IInstructions,
    sessionId: string | number
  ): void {
    const currentUserId = this.#profileService.userProfile()?.profile.name!;

    // Remove the "Waiting" message if any for this session (atomically)
    this.#chatSessionService.chatSessions.update(sessions => {
      if (!sessions) return sessions;
      return sessions.map(s =>
        s.id === sessionId
          ? { ...s, messages: (s.messages ?? []).filter(msg => msg.id !== ExecutionStatusEnum.Waiting) }
          : s
      );
    });

    // Update active session if needed
    if (this.#isActiveSession(sessionId)) {
      const session = this.#chatSessionService.chatSessions()?.find(s => s.id === sessionId);
      if (session?.messages) {
        this.#chatMessageService.messages.set([...session.messages]);
      }
    }

    if (
      !this.#checkIfInstructionIdExists(
        workflowExecutionState.workflowExecutionId,
        sessionId
      )
    ) {
      this.#addMessageToSession(sessionId, {
        senderId: ChatRole.System,
        role: ChatRole.System,
        content: "The workflow was stopped by the user.",
        canSubmit: false,
        canCopy: true,
        currentUserId,
        id: workflowExecutionState.workflowExecutionId,
      });
    }

    // Update the workflow execution status to Cancelled
    const sessionState = this.#automationStateService.getSessionState(sessionId);
    if (sessionState?.instructionsExecutionState) {
      this.#automationStateService.updateSessionState(sessionId, {
        instructionsExecutionState: {
          ...sessionState.instructionsExecutionState,
          status: ExecutionStatusEnum.Cancelled,
          workflowExecutionStatus: ExecutionStatusEnum.Cancelled,
        },
      });
    }

    // TODO: Make a call to the API to cancel the workflow

    if (this.#isActiveSession(sessionId)) {
      this.updateChatSession();
    }
  }

  #persistSessionById(sessionId: string | number): void {
    const sessions = this.#chatSessionService.chatSessions();
    if (!sessions) {
      return;
    }

    const session = sessions.find((s) => s.id === sessionId);
    if (!session) {
      return;
    }

    const requestBody: IChatSession = {
      id: session.id,
      label: session.label,
      version: session.version,
      agents: session.agents,
      temperature: session.temperature,
      topP: session.topP,
      model: session.model,
      isValid: session.isValid,
      messages: session.messages ?? [],
      sensitivityLabels: session.sensitivityLabels,
      snapshotConfiguration: session.snapshotConfiguration || null,
      lastStatus: session.lastStatus,
    };

    this.#apiService.persistSession(requestBody).subscribe({
      next: () => {},
      error: (error: any) => {
        console.error("Failed to persist chat session.", error);
      },
    });
  }

  /**
   * Handles the transition to the next workflow instruction.
   *
   * - If there is a next instruction, updates workflow state and chat messages based on the instruction type.
   * - Sets waiting flags for user input or file upload as needed.
   * - Adds the next instruction's prompt to the chat if appropriate.
   * - Updates the current instruction index and in-progress instruction.
   * - If there is no next instruction, finalizes the workflow execution.
   *
   * @param instruction The current workflow instruction execution object.
   * @param currentUserId The ID of the current user.
   */
  async #handleNextInstruction(
    instruction: IWorkflowInstructionExecution,
    currentUserId: string,
    sessionId: string | number
  ): Promise<void> {
    if (instruction.nextInstruction) {
      // Check if the NEXT IMMEDIATE instruction is a user input type
      const nextType = instruction.nextInstruction.details.type;
      const nextIsUserInput =
        nextType === InstructionType.CollectInformation ||
        nextType === InstructionType.FileUpload;

      if (nextIsUserInput) {
        // GUARD: Check if the session is already paused for this EXACT instruction ID
        // This prevents re-pausing when polling receives stale/delayed API responses after user submits input
        const currentInstructionInProgress = this.#multiSessionService.getSessionInstructionInProgress(sessionId);
        const currentInstructionId = this.#multiSessionService.getSessionInstructionId(sessionId);
        const isAlreadyPausedForThisInstruction =
          currentInstructionId === instruction.nextInstruction.id ||
          currentInstructionInProgress?.id === instruction.nextInstruction.id;

        if (isAlreadyPausedForThisInstruction) {
          return;
        }

        // Determine user input type and update session state
        const isCollectInfo = nextType === InstructionType.CollectInformation;
        const userInputType = isCollectInfo ? InstructionType.CollectInformation : InstructionType.FileUpload;

        // Increment index and update state in one call
        const nextIndex = this.#getNextInstructionIndex(instruction, sessionId);
        this.#automationStateService.updateSessionState(sessionId, {
          isWaitingForUserInput: isCollectInfo,
          isWaitingForFileUpload: !isCollectInfo,
          isWorkflowInProgress: false,
          currentInstructionIndex: nextIndex,
          instructionInProgress: instruction.nextInstruction,
        });

        // Pause the session and queue dialog opening
        const sessionState = this.#automationStateService.getSessionState(sessionId);
        const workflowId = sessionState?.workflowExecutionState?.workflowExecutionId;

        this.#multiSessionService.pauseSessionForUserInput(
          sessionId,
          userInputType,
          workflowId,
          instruction.nextInstruction.id,
          instruction.nextInstruction
        );

        this.#queueUserInputDialogIfNeeded(sessionId, userInputType);
      } else {
        // For AI Prompt and Agent instruction types, add prompt if it doesn't exist
        const promptAlreadyExists = this.#checkIfPromptForInstructionExists(
          instruction.nextInstruction.id,
          sessionId
        );

        if (!promptAlreadyExists) {
          const promptMessageId = `${instruction.nextInstruction.id}-prompt-${Date.now()}`;
          this.#addMessageToSession(sessionId, {
            senderId: currentUserId,
            role: ChatRole.User,
            content: instruction.nextInstruction.details.prompt,
            canSubmit: true,
            canCopy: true,
            currentUserId,
            id: promptMessageId,
          });
          if (this.#isActiveSession(sessionId)) {
            await this.updateChatSessionConfiguration(instruction.nextInstruction);
          }
        }

        // Increment index and update state
        const nextIndex = this.#getNextInstructionIndex(instruction, sessionId);
        this.#automationStateService.updateSessionState(sessionId, {
          currentInstructionIndex: nextIndex,
          instructionInProgress: instruction.nextInstruction,
        });
      }

      if (this.#isActiveSession(sessionId)) {
        this.updateChatSession();
      }
    } else {
      if (this.#isActiveSession(sessionId)) {
        await this.updateChatSessionConfiguration();
        await this.updateChatSession();
        this.endWorkflowExecution(undefined, sessionId);
      } else {
        this.endWorkflowExecution(false, sessionId);
      }
    }
  }

  /**
   * Handles the case when a workflow instruction is waiting for execution.
   *
   * - Adds the instruction prompt and a "Processing..." message to the chat if not already present.
   * - Persists the updated chat session if new messages are added.
   *
   * @param instruction The current workflow instruction execution object.
   * @param currentUserId
   * @param messages
   */
  #generateWaitingMessage(
    instruction: IWorkflowInstructionExecution,
    currentUserId: string,
    messages: IMessage[],
    sessionId: string | number
  ): void {
    // Update session icon
    this.#uiLayoutService.updateSessionStatusIcon(sessionId, SessionStatus.PROCESSING);
    // Add a "Processing" message to the chat session messages if it doesn't already exist.
    const waitingMessageExists = this.#checkIfInstructionIdExists(
      ExecutionStatusEnum.Waiting,
      sessionId
    );
    if (!waitingMessageExists) {
      this.#addMessageToSession(sessionId, {
        senderId: ChatRole.Assistant,
        role: ChatRole.Assistant,
        content: "Processing...",
        canSubmit: false,
        canCopy: false,
        currentUserId,
        id: ExecutionStatusEnum.Waiting,
      });
    }
    if (!waitingMessageExists) {
      if (this.#isActiveSession(sessionId)) {
        this.updateChatSession();
      }
    }
  }

  /**
   * Handles the case when a workflow instruction execution succeeds.
   *
   * - Removes the "Waiting" message from the chat.
   * - Adds the AI response to the chat if not already present.
   * - Handles the transition to the next instruction or ends the workflow if complete.
   *
   * @param instruction The current workflow instruction execution object.
   * @param currentUserId
   */
  #generateSuccessMessage(
    instruction: IWorkflowInstructionExecution,
    currentUserId: string,
    sessionId: string | number
  ): void {

    // Remove the "Waiting" message (atomically)
    this.#chatSessionService.chatSessions.update(sessions => {
      if (!sessions) return sessions;
      return sessions.map(s =>
        s.id === sessionId
          ? { ...s, messages: (s.messages ?? []).filter(msg => msg.id !== ExecutionStatusEnum.Waiting) }
          : s
      );
    });

    // Update active session if needed
    if (this.#isActiveSession(sessionId)) {
      const session = this.#chatSessionService.chatSessions()?.find(s => s.id === sessionId);
      if (session?.messages) {
        this.#chatMessageService.messages.set([...session.messages]);
      }
    }
    const outputId = instruction.output?.id;
    if (!outputId) {
      // If outputId is undefined, skip adding the message.
      if (this.#isActiveSession(sessionId)) {
        this.updateChatSessionConfiguration();
        this.endWorkflowExecution(undefined, sessionId);
        this.updateChatSession();
      } else {
        this.endWorkflowExecution(false, sessionId);
      }
      return;
    }
    // Only add the instruction output message if it doesn't exist
    if (!this.#checkIfInstructionIdExists(outputId, sessionId)) {
      this.#addMessageToSession(
        sessionId,
        {
          senderId: ChatRole.Assistant,
          role: ChatRole.Assistant,
          content: instruction.output.message.content,
          canSubmit: true,
          canCopy: true,
          requestDetail: instruction.output.requestDetail || undefined,
          currentUserId,
          id: outputId,
        },
        false
      );
    }

    // Always handle next instruction to progress the workflow
    // (duplicate checks inside #handleNextInstruction prevent re-adding prompts)
    this.#handleNextInstruction(instruction, currentUserId, sessionId);
  }

  /**
   * Handles the case when a workflow instruction execution fails.
   *
   * - Removes the "Waiting" message from the chat.
   * - Adds a system message indicating failure if not already present.
   * - Updates the chat session configuration and ends the workflow execution.
   * - Persists the updated chat session.
   *
   * @param instruction The current workflow instruction execution object.
   * @param currentUserId
   */
  #generateFailedMessage(
    instruction: IWorkflowInstructionExecution,
    currentUserId: string,
    sessionId: string | number
  ): void {
    // Remove the "Waiting" message (atomically)
    this.#chatSessionService.chatSessions.update(sessions => {
      if (!sessions) return sessions;
      return sessions.map(s =>
        s.id === sessionId
          ? { ...s, messages: (s.messages ?? []).filter(msg => msg.id !== ExecutionStatusEnum.Waiting) }
          : s
      );
    });

    // Update active session if needed
    if (this.#isActiveSession(sessionId)) {
      const session = this.#chatSessionService.chatSessions()?.find(s => s.id === sessionId);
      if (session?.messages) {
        this.#chatMessageService.messages.set([...session.messages]);
      }
    }
    let contentMessage = '';

    // 5590 - instructions that fail now may have content in the form of a user-friendly error message
    if (instruction.output?.message?.content && instruction.output?.message?.content.trim() !== '') {
      contentMessage = `The workflow instruction has failed to finish executing with the following message:\n\n${instruction.output.message.content}`;
    } else {
      contentMessage = "The workflow instruction has failed to finish executing";
    }

    if (!this.#checkIfInstructionIdExists(instruction.id, sessionId)) {
      this.#addMessageToSession(
        sessionId,
        {
          senderId: ChatRole.System,
          role: ChatRole.System,
          content: contentMessage,
          canSubmit: false,
          canCopy: true,
          currentUserId,
          id: instruction.id,
        },
        false
      );
    }
    if (this.#isActiveSession(sessionId)) {
      this.updateChatSessionConfiguration();
      this.endWorkflowExecution(undefined, sessionId, ExecutionStatusEnum.Failed);
      this.updateChatSession();
    } else {
      this.endWorkflowExecution(undefined, sessionId, ExecutionStatusEnum.Failed);
    }
  }

  /**
   * Checks if a message with the specified instruction ID exists in the current chat messages.
   *
   * @param instructionId - The ID of the instruction to check for.
   * @returns `true` if a message with the given instruction ID exists, otherwise `false`.
   */
  #checkIfInstructionIdExists(instructionId: string, sessionId: string | number): boolean {
    const updatedMessages = this.#getMessagesForSession(sessionId);
    return updatedMessages.some((msg) => msg.id === instructionId);
  }

  /**
   * Checks if a prompt message for the specified instruction already exists in the chat.
   * This checks for any message ID that starts with the instruction ID followed by "-prompt-",
   * which catches prompts added with timestamp-based unique IDs.
   *
   * @param instructionId - The ID of the instruction to check for.
   * @param sessionId - The session ID.
   * @returns `true` if a prompt for the given instruction exists, otherwise `false`.
   */
  #checkIfPromptForInstructionExists(instructionId: string, sessionId: string | number): boolean {
    const updatedMessages = this.#getMessagesForSession(sessionId);
    const promptPrefix = `${instructionId}-prompt-`;
    return updatedMessages.some((msg) => msg.id?.startsWith(promptPrefix));
  }

  /**
   * Calculates the next instruction index for the workflow.
   * Uses the current state index instead of the instruction.index from API response to avoid stale values.
   *
   * @param instruction The current workflow instruction execution object.
   * @param sessionId The session ID.
   * @returns The next instruction index.
   */
  #getNextInstructionIndex(instruction: IWorkflowInstructionExecution, sessionId: string | number): number {
    const currentStateIndex = this.#automationStateService.getSessionState(sessionId)?.currentInstructionIndex ?? instruction.index;
    return currentStateIndex + 1;
  }

  /**
   * Adds a user input request to the queue to trigger dialog opening.
   * Prevents duplicate dialog requests for the same session.
   *
   * @param sessionId The session ID.
   * @param inputType The type of user input needed.
   */
  #queueUserInputDialogIfNeeded(
    sessionId: string | number,
    inputType: InstructionType.CollectInformation | InstructionType.FileUpload
  ): void {
    this.userInputNeeded.update(queue => {
      const alreadyQueued = queue.some(item => item.sessionId === sessionId);
      return alreadyQueued ? queue : [...queue, { sessionId, inputType }];
    });
  }

  /**
   * Unsubscribes from the instruction execution polling subscription and cleans up resources.
   *
   * This method stops the workflow instruction polling by unsubscribing from the active subscription,
   * ensuring no further polling occurs and preventing memory leaks.
   */
  #stopInstructionExecution(sessionId: string | number): void {
    const subscription = this.#instructionExecutionSubscriptions.get(sessionId);
    if (subscription) {
      subscription.unsubscribe();
      this.#instructionExecutionSubscriptions.delete(sessionId);
    }
  }

  /**
   * Checks if any of the instructions in a workflow has a model that is available for use.
   * For any instruction whose model is unavailable, adds a system message listing each
   * affected instruction and its model in bullet-point format.
   * @param workflow The workflow object containing instructions to check.
   * @returns true if all instructions have available models, false if any instructions has an unavailable model
   */
  async areInstructionModelsAvailable(workflow: IWorkflow): Promise<boolean> {
    const instructionIds = workflow.details.instructions ?? [];
    if (instructionIds.length === 0) {
      return true;
    }

    const instructions = await firstValueFrom(
      forkJoin(
        instructionIds.map(id =>
          this.#apiService.getInstructionById(id).pipe(
            catchError(() => of(null))
          )
        )
      )
    );

    const availabilityResults = await firstValueFrom(
      forkJoin(
        instructions.map((instruction, index) => {
          if (!instruction) {
            return of({ id: instructionIds[index], name: 'Unknown', model: null, isAvailable: false });
          }
          return this.#utilitiesService.isModelAvailable(instruction.details.model).pipe(
            map(isAvailable => ({
              id: instruction.id,
              name: instruction.details.name,
              model: instruction.details.model,
              isAvailable
            })),
            catchError(() => of({
              id: instruction.id,
              name: instruction.details.name,
              model: instruction.details.model,
              isAvailable: false
            }))
          );
        })
      )
    );

    const unavailable = availabilityResults.filter(result => !result.isAvailable);

    if (unavailable.length === 0) {
      return true;
    }

    const bulletPoints = unavailable
      .map(result => `\n• Instruction "${result.name}": model "${result.model?.model ?? 'Unknown'}"}) is no longer available.`)
      .join('\n');

    const unavailableInstructionModelsMessage =
      `The following workflow instructions have unavailable models. Please update the instructions and try again:\n${bulletPoints}`;

      this.#chatMessageService.addMessage({
        senderId: ChatRole.System,
        role: ChatRole.System,
        content: unavailableInstructionModelsMessage,
        system: true,
        error: true,
        currentUserId: this.#profileService.userProfile()?.profile.name!,
        showExpansionPanel: true,
        expansionPanelTitle: 'Error Executing Workflow',
      });

      // Persist the session with the error message
      this.#persistSessionById(this.#chatSessionService.selectedSessionId());

    return false;
  }
}
